
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { mockProducts } from '../../mocks/products';

const categories = ['All', ...Array.from(new Set(mockProducts.map((p) => p.category)))];

export default function WebsitesPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = mockProducts.filter((p) => {
    const matchCategory = activeCategory === 'All' || p.category === activeCategory;
    const matchSearch =
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="min-h-screen bg-carbon-950">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-neon/10 border border-neon/20 rounded-full mb-6">
            <i className="ri-fire-line text-neon text-sm"></i>
            <span className="text-neon text-xs font-semibold tracking-wide uppercase">
              Exclusive Deals
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Top Affiliate <span className="text-neon">Deals</span>
          </h1>
          <p className="text-white/50 text-base max-w-xl mx-auto mb-10">
            Hand‑picked software deals with the best commissions. Start earning with tools trusted by
            thousands.
          </p>

          {/* Search */}
          <div className="max-w-lg mx-auto relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/50"></i>
            <input
              type="text"
              placeholder="Search deals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-none text-white placeholder-white/50 text-sm focus:outline-none focus:border-neon/50 transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-6 lg:px-8 pb-6">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2 justify-center">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-none text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === cat
                  ? 'bg-neon text-black'
                  : 'bg-white/5 text-white/50 hover:text-white hover:bg-white/10 border border-white/10'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Deals Grid */}
      <section className="px-6 lg:px-8 pb-24">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-2xl text-white/20"></i>
              </div>
              <p className="text-white/40 text-sm">No deals found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {filtered.map((product) => (
                <div
                  key={product.id}
                  className="bg-white/[0.03] border border-white/5 rounded-none overflow-hidden hover:border-neon/20 transition-all group"
                >
                  <Link to={`/websites/${product.id}`} className="block cursor-pointer">
                    <div className="relative h-48 bg-white/[0.02] overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`${product.name} affiliate deal`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm text-white/80 text-xs font-medium rounded-none">
                        {product.category}
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-bold text-white mb-1.5 group-hover:text-neon transition-colors">
                        {product.name}
                      </h3>
                      <p className="text-white/40 text-sm mb-5 leading-relaxed">{product.description}</p>

                      <div className="flex items-center gap-2 mb-5 pt-4 border-t border-white/5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-none text-xs font-semibold ${
                            product.clicks > 3500
                              ? 'bg-neon/10 text-neon'
                              : product.clicks > 2500
                              ? 'bg-amber-500/10 text-amber-400'
                              : 'bg-white/5 text-white/50'
                          }`}
                        >
                          <i
                            className={`${
                              product.clicks > 3500
                                ? 'ri-fire-fill'
                                : product.clicks > 2500
                                ? 'ri-thumb-up-fill'
                                : 'ri-star-line'
                            } text-xs`}
                          ></i>
                          {product.clicks > 3500
                            ? 'Most Popular'
                            : product.clicks > 2500
                            ? 'Trending'
                            : 'Recommended'}
                        </span>
                        <div className="flex items-center gap-1 ml-auto">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          <span className="text-white/60 text-xs font-medium">{product.rating}</span>
                          <span className="text-white/30 text-xs">
                            ({product.reviews?.toLocaleString()})
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>

                  <div className="px-5 pb-5">
                    <a
                      href={product.affiliateLink}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="block w-full py-2.5 bg-neon/10 text-neon text-sm font-semibold text-center rounded-none hover:bg-neon hover:text-black transition-all whitespace-nowrap cursor-pointer"
                    >
                      Get This Deal <i className="ri-arrow-right-up-line ml-1"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
